// Auth for the Vendor OS frontend.
//
// Two modes from one SPA:
//  1. Staff  — Google Sign-In via Google Identity Services (GIS). The
//              `credential` returned by GIS *is* the Google ID token. We
//              stash it in localStorage and send it in every API call body
//              for GAS to verify via tokeninfo.
//  2. Vendor — Magic-link token in the URL: ?t=VND-1042.<payload>.<hmac>.
//              We persist it to localStorage and pass it on every API call.

const STAFF_TOKEN_KEY  = 'vendoros:staff_id_token';
const VENDOR_TOKEN_KEY = 'vendoros:vendor_token';
const STAFF_PROFILE_KEY = 'vendoros:staff_profile';

export function useMagicLinkFromUrl() {
  const url = new URL(window.location.href);
  const t = url.searchParams.get('t');
  if (t) {
    localStorage.setItem(VENDOR_TOKEN_KEY, t);
    // A fresh magic link means a new session — reset the login-confirmation
    // flag so the supplier sees the welcome / sign-in screen.
    sessionStorage.removeItem('vendoros:portal_session_confirmed');
    // Critical: clear any prior staff session. The magic link becomes the
    // dominant identity — vendors must never be able to flip back to the
    // internal view just because they opened the link in a window where
    // staff was previously signed in.
    localStorage.removeItem(STAFF_TOKEN_KEY);
    localStorage.removeItem(STAFF_PROFILE_KEY);
    url.searchParams.delete('t');
    window.history.replaceState({}, '', url.toString());
  }
}

export function hasVendorToken() {
  return !!localStorage.getItem(VENDOR_TOKEN_KEY);
}

/**
 * Establish a supplier session from a bearer token returned by api_login /
 * api_setPassword. Mirrors a magic-link consumption: the vendor token becomes
 * the dominant identity (any prior staff session is cleared) and the portal is
 * marked confirmed so the user lands straight in their dashboard.
 */
export function setVendorSession(token) {
  if (!token) return;
  localStorage.setItem(VENDOR_TOKEN_KEY, token);
  localStorage.removeItem(STAFF_TOKEN_KEY);
  localStorage.removeItem(STAFF_PROFILE_KEY);
  sessionStorage.setItem('vendoros:portal_session_confirmed', '1');
}

/**
 * Read a set/reset-password token from ?pw=<token>, strip it from the URL, and
 * return it. Distinct from the ?t= magic-link param so the SPA can route to the
 * "set your password" screen instead of signing straight in.
 */
export function readPwLinkFromUrl() {
  const url = new URL(window.location.href);
  const pw = url.searchParams.get('pw');
  if (!pw) return null;
  url.searchParams.delete('pw');
  window.history.replaceState({}, '', url.toString());
  return pw;
}

/**
 * Read a &kyc=1 hint from a KYC-submission link, strip it from the URL, and
 * remember (for this session) that the supplier should land on the Documents /
 * KYC-upload screen instead of the dashboard.
 */
export function consumeKycLanding() {
  const url = new URL(window.location.href);
  if (url.searchParams.get('kyc')) {
    url.searchParams.delete('kyc');
    window.history.replaceState({}, '', url.toString());
    sessionStorage.setItem('vendoros:land_kyc', '1');
  }
}
export function shouldLandOnKyc() {
  return sessionStorage.getItem('vendoros:land_kyc') === '1';
}
export function clearKycLanding() {
  sessionStorage.removeItem('vendoros:land_kyc');
}

export function isPortalSessionConfirmed() {
  return sessionStorage.getItem('vendoros:portal_session_confirmed') === '1';
}
export function confirmPortalSession() {
  sessionStorage.setItem('vendoros:portal_session_confirmed', '1');
}

export function getCurrentToken() {
  // Vendor-FIRST, to match getCurrentContext() (which returns 'portal' whenever a
  // vendor token exists). If a supplier ever holds both a magic-link/vendor token
  // AND a stale staff (Google) token, portal API calls must send the VENDOR token.
  // The old staff-first order would send the Google token while the app is in
  // portal context → the backend rejects it as "not on allowlist".
  return localStorage.getItem(VENDOR_TOKEN_KEY)
      || localStorage.getItem(STAFF_TOKEN_KEY)
      || null;
}

export function hasStaffToken() {
  return !!localStorage.getItem(STAFF_TOKEN_KEY);
}

/** Drop only the staff (Google) session, leaving any vendor token untouched. */
export function clearStaffSession() {
  localStorage.removeItem(STAFF_TOKEN_KEY);
  localStorage.removeItem(STAFF_PROFILE_KEY);
  if (window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
}

/**
 * Read-and-clear the "this Google account isn't staff" flag set when a non-staff
 * Google sign-in is rejected by the backend (see api.js). One-shot: the login
 * screen reads it to open in Supplier mode and explain what happened.
 */
export function consumeNotStaff() {
  const v = sessionStorage.getItem('vendoros:not_staff');
  if (v) sessionStorage.removeItem('vendoros:not_staff');
  return v === '1';
}

export function getCurrentContext() {
  if (localStorage.getItem(VENDOR_TOKEN_KEY)) return 'portal';
  if (localStorage.getItem(STAFF_TOKEN_KEY))  return 'internal';
  return 'anonymous';
}

export function getStaffProfile() {
  const raw = localStorage.getItem(STAFF_PROFILE_KEY);
  return raw ? JSON.parse(raw) : null;
}

/* ---------- Google Identity Services (GIS) ---------- */

let gsiReady = null;       // Promise that resolves when the script + init are done
let signInListeners = [];  // fns called after a successful sign-in

function loadGsiScript() {
  if (document.getElementById('gsi-script')) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = 'gsi-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
}

export function initGoogleAuth() {
  if (gsiReady) return gsiReady;
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId || clientId.startsWith('REPLACE_ME')) {
    console.warn('VITE_GOOGLE_CLIENT_ID not set — sign-in disabled');
    return (gsiReady = Promise.reject(new Error('Client ID not configured')));
  }
  gsiReady = loadGsiScript().then(() => {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
      auto_select: false,
      ux_mode: 'popup',
    });
  });
  return gsiReady;
}

function handleCredentialResponse(response) {
  if (!response.credential) return;
  const idToken = response.credential;
  // Quick payload parse so we can show "Hi <name>" without a round-trip.
  const profile = parseJwtPayload(idToken);
  localStorage.setItem(STAFF_TOKEN_KEY, idToken);
  if (profile) {
    localStorage.setItem(STAFF_PROFILE_KEY, JSON.stringify({
      email: profile.email,
      name:  profile.name,
      picture: profile.picture,
    }));
  }
  signInListeners.forEach(fn => { try { fn(); } catch (e) {} });
}

function parseJwtPayload(jwt) {
  try {
    const [, payload] = jwt.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
}

export function onSignIn(fn) {
  signInListeners.push(fn);
  return () => { signInListeners = signInListeners.filter(f => f !== fn); };
}

export async function renderSignInButton(el, options = {}) {
  await initGoogleAuth();
  window.google.accounts.id.renderButton(el, {
    theme: 'outline',
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    logo_alignment: 'left',
    ...options,
  });
}

export function signOut() {
  localStorage.removeItem(STAFF_TOKEN_KEY);
  localStorage.removeItem(STAFF_PROFILE_KEY);
  localStorage.removeItem(VENDOR_TOKEN_KEY);
  sessionStorage.removeItem('vendoros:portal_session_confirmed');
  if (window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
  window.location.reload();
}

/**
 * Force the user back to the sign-in screen because their session is no longer
 * valid — token expired, magic link expired, or the server rejected the token.
 * Unlike the silent red-banner behaviour, this drops every credential and
 * returns to a fresh sign-in: clear all tokens, remember which login surface to
 * reopen, flag that the session expired (the login screen shows a notice), then
 * reload so the SPA re-renders on the unauthenticated path. Distinct from a
 * user-initiated signOut() so the login screen can explain *why* they're back.
 */
export function forceReauth() {
  const wasPortal = getCurrentContext() === 'portal';
  localStorage.removeItem(STAFF_TOKEN_KEY);
  localStorage.removeItem(STAFF_PROFILE_KEY);
  localStorage.removeItem(VENDOR_TOKEN_KEY);
  sessionStorage.removeItem('vendoros:portal_session_confirmed');
  // Value doubles as the login mode to reopen ('supplier' | 'internal').
  sessionStorage.setItem('vendoros:session_expired', wasPortal ? 'supplier' : 'internal');
  if (window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
  window.location.reload();
}

/**
 * Read-and-clear the session-expired flag set by forceReauth(). Returns the
 * login mode to reopen ('supplier' | 'internal') when a session just expired,
 * or null otherwise. One-shot: the flag is removed on read.
 */
export function consumeSessionExpired() {
  const v = sessionStorage.getItem('vendoros:session_expired');
  if (v) sessionStorage.removeItem('vendoros:session_expired');
  return v || null;
}
